import {
  GROUP_DELETE_SUCCESS,
  LOAD_SAVED_STATE,
  LOAD_SAVED_STATE_ERROR,
  PLAYLIST_DELETE_BATCH_SUCCESS,
  PLAYLIST_VERSION_DELETE_SUCCESS,
  SHORTLIST_ACTIVATE,
  SHORTLIST_CLEAR,
  SHORTLIST_DEACTIVATE,
  SHORTLIST_DELETE,
  SHORTLIST_NEW,
  SHORTLIST_NEW_START_CREATE,
  SHORTLIST_PLAYLIST_TRACKS_ADD,
  SHORTLIST_RESET,
  SHORTLIST_SECTION_ADD,
  SHORTLIST_SECTION_REMOVE,
  SHORTLIST_SECTION_UPDATE,
  SHORTLIST_SET_LOADING,
  SHORTLIST_TEMP_ITEM_INSERT,
  SHORTLIST_TEMP_ITEM_PROGRESS_UPDATE,
  SHORTLIST_TEMP_ITEM_REMOVE,
  SHORTLIST_TEMP_ITEM_UPDATE,
  SHORTLIST_UPDATE,
  SHORTLIST_UPLOADS_CANCEL,
  START_SAVING_STATE,
  TRACK_DELETE_BATCH_SUCCESS,
  TRACK_MERGE_SUCCESS,
} from '@disco/constants/ActionTypes'
import { __REACT_NATIVE__ } from '@disco/constants/environment'
import { deleteItem, updateItem } from '@disco/utils/immutable'
import { stripFiles } from '@disco/utils/upload'
import cuid from 'cuid'
import lodashFind from 'lodash/find'
import findLastIndex from 'lodash/findLastIndex'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import { createReducer } from 'redux-create-reducer'
import type { Group } from 'types/group'
import type { PlaylistId } from 'types/playlist'
import type {
  Shortlist,
  ShortlistSection,
  ShortlistTrackItem,
} from 'types/shortlist'
import type { Track, TrackId } from 'types/track'
import { adjustSectionsOnRemovedTrackIndexes } from '@disco/state-management/actions/ShortlistActions'
const initialState: State = {
  loading: false,
  loaded: Boolean(__REACT_NATIVE__),
  // state is persisted on mobile
  error: null,
  shortlistActivation: null,
  visibleItems: [],
  byId: {},
  shortlistCreating: {},
}
export type State = {
  loading: boolean
  loaded: boolean
  error: any
  shortlistActivation: string | null | undefined
  visibleItems: string[]
  byId: ShortlistDictionary
  shortlistCreating: Record<PlaylistId, boolean>
}
type ShortlistDictionary = { [key: string]: Shortlist | undefined }

function adjustSectionsOnRemovedTracks(
  shortlist: Shortlist,
  trackIds: TrackId[],
): ShortlistSection[] {
  if (
    shortlist.sections.length === 0 ||
    !shortlist.items.some(item => trackIds.indexOf(item.track.id) > -1)
  ) {
    return shortlist.sections
  }

  let adjustedSections = shortlist.sections.slice()

  for (let i = shortlist.items.length - 1; i >= 0; i--) {
    // i is within bounds of shortlist.items since it starts at length-1
    const trackId = shortlist.items[i]!.track.id
    if (trackIds.indexOf(trackId) === -1) continue
    // adjust section positions after removed track
    adjustedSections = adjustedSections.map(s => {
      if (s.position > i) return { ...s, position: s.position - 1 }
      return s
    })
  }

  return adjustedSections
}

// TODO: Replace with adjustSectionsOnMovedTrackIndexes from '@disco/state-management/actions/ShortlistActions'
function adjustSectionsOnMovedTracks(
  shortlist: Shortlist,
  tracks: Track[],
  position: number,
  targetSection: ShortlistSection,
) {
  const removedItems = shortlist.items.filter(item => {
    return tracks.some(track => track.id === item.track.id)
  })
  const targetSectionIndex = targetSection
    ? shortlist.sections.findIndex(s => s.id === targetSection.id)
    : -1
  const adjustmentOffsets = shortlist.sections.map(s => {
    return removedItems.filter(item => {
      return item.id < s.position
    }).length
  })
  return shortlist.sections.map((s, sIndex) => {
    // sIndex is within adjustmentOffsets since both are derived from shortlist.sections
    const normalizedPosition = s.position - adjustmentOffsets[sIndex]!
    const skipCorrection = targetSection
      ? sIndex <= targetSectionIndex
      : normalizedPosition < position
    const correctedLength = skipCorrection ? 0 : tracks.length
    return { ...s, position: normalizedPosition + correctedLength }
  })
}

const adjustItemIds = <T>(items: T[]): T[] =>
  items.map((item, index) => ({ ...item, id: index }))

const shortlistTemplate = {
  album_image_url: null,
  color: '',
  album_image: null,
  isActive: true,
  is_client_version: false,
  download_links: true,
  show_lyrics: false,
  show_business_logo: null,
  show_contacts: true,
  showcase: null,
  include_nested_track_versions: true,
  share_stems: false,
  items: [],
  tempItems: [],
  title: '',
  saved: false,
  groups: [],
  groupSearchResults: [],
  sections: [],
  tags: [],
  playlist_id: null,
  general_message: null,
  general_message_html: null,
  sort_key: 'custom',
  sort_direction: null,
  track_aliases: null,
  playlist_aliases: null,
  user_aliases: null,
}

const updateShortlist = (state: State, shortlist: any): State => {
  return { ...state, byId: { ...state.byId, [shortlist.id]: shortlist } }
}

const transformShortlists = (
  state: State,
  updaterFunc: (shortlist: Shortlist) => Shortlist,
): State => {
  const dict = Object.keys(state.byId).reduce((acc, key) => {
    const shortlist = state.byId[key]
    // key is from Object.keys(state.byId), so shortlist is defined
    return { ...acc, [key]: updaterFunc(shortlist!) }
  }, {})
  return {
    ...state,
    byId: dict,
  }
}

// function compares track and shortlist item by id if it's present
// if not it will compare track and item by temporary/upload id if they are exist
const shortlistItemTrackComparator = (
  item: ShortlistTrackItem,
  track: Track,
): boolean =>
  track.id === item.track.id ||
  Boolean(
    track.tempId && item.track.uploadId && track.tempId === item.track.uploadId,
  )

export const shortlistsReducer = createReducer<State, any>(initialState, {
  [SHORTLIST_SET_LOADING](state, { loading }) {
    return { ...state, loading }
  },

  [SHORTLIST_NEW_START_CREATE](
    state: State,
    action: {
      result: {
        id: string
      }
    },
  ): State {
    if (!action.result.id) {
      return state
    }

    return {
      ...state,
      shortlistCreating: {
        ...state.shortlistCreating,
        [action.result.id]: true,
      },
    }
  },

  [SHORTLIST_NEW](
    state: State,
    action: {
      result: Shortlist
    },
  ): State {
    const updateShortlistСreating = { ...state.shortlistCreating }

    if (action?.result?.playlist_id) {
      updateShortlistСreating[action.result.playlist_id] = false
    }

    return {
      ...updateShortlist(state, { ...shortlistTemplate, ...action.result }),
      visibleItems: action.result.hidden
        ? state.visibleItems
        : [action.result.id, ...state.visibleItems],
      shortlistCreating: { ...updateShortlistСreating },
    }
  },

  [SHORTLIST_DELETE](state: State, { shortlistId }): State {
    return {
      ...state,
      visibleItems: state.visibleItems.filter(id => id !== shortlistId),
      byId: omit(state.byId, shortlistId),
    }
  },

  [SHORTLIST_CLEAR](
    state: State,
    { shortlistId, defaultGroups, default_zip_mode, share_mode },
  ): State {
    // clear shortlist context, leave only groups
    const newShortlist = {
      ...shortlistTemplate,
      id: shortlistId,
      groups: defaultGroups || [],
      default_zip_mode,
      share_mode,
    }
    return updateShortlist(state, newShortlist)
  },

  [SHORTLIST_UPDATE](
    state: State,
    {
      shortlistId,
      result,
    }: {
      shortlistId: string
      result: Shortlist
    },
  ): State {
    const shortlist = state.byId[shortlistId]
    if (!shortlist) return state
    // Computed defaults that result can override. Using a spread object rather than
    // explicit named keys avoids TS2783 (duplicate-key error when a named property
    // precedes a spread of an object that statically contains the same key).
    const defaults = {
      sort_key: result.items ? 'custom' : shortlist.sort_key,
      sort_direction: result.items ? null : shortlist.sort_direction,
      track_aliases:
        result.items && shortlist.track_aliases
          ? pick(
              shortlist.track_aliases,
              result.items.map(item => item.track.id),
            )
          : shortlist.track_aliases,
    }
    const updatedShortlist = {
      ...shortlist,
      saved: false,
      ...defaults,
      ...result,
      showcase: shortlist.showcase
        ? { ...shortlist.showcase, ...result.showcase }
        : result.showcase,
    }
    return updateShortlist(state, updatedShortlist)
  },

  // Removing a group from playlists in shortlists
  [GROUP_DELETE_SUCCESS](
    state: State,
    {
      group,
    }: {
      group: Group
    },
  ): State {
    let updatedState = state
    Object.keys(state.byId).forEach(key => {
      // key is from Object.keys(state.byId), so shortlist is defined
      const shortlist = state.byId[key]!
      const index = shortlist.groups.findIndex(({ id }) => id === group.id)

      if (index !== -1) {
        const groups = [
          ...shortlist.groups.slice(0, index),
          ...shortlist.groups.slice(index + 1),
        ]
        updatedState = updateShortlist(updatedState, { ...shortlist, groups })
      }
    })
    return updatedState
  },

  [SHORTLIST_TEMP_ITEM_UPDATE](
    state: State,
    { shortlistId, tempItem, finalUpdate },
  ): State {
    const shortlist: Shortlist | null | undefined = state.byId[shortlistId]
    if (!shortlist) return state
    const { tempItems, items } = shortlist
    const tempItemIndex = (tempItems ?? []).findIndex(
      t => t.tempId === tempItem.tempId,
    )
    const itemIndex = (items ?? []).findIndex(
      t => t.track.tempId === tempItem.tempId,
    )
    // ignore update if item no longer exists
    if (itemIndex === -1 && (tempItemIndex === -1 || !tempItems)) return state
    let newTempItems = tempItems

    if (tempItemIndex > -1 && tempItems) {
      newTempItems = finalUpdate
        ? deleteItem(tempItems, tempItemIndex)
        : updateItem(tempItems, tempItemIndex, {
            ...tempItems[tempItemIndex],
            ...tempItem,
          })
    }

    let newItems = items

    if (itemIndex > -1 && finalUpdate) {
      newItems = updateItem(items, itemIndex, {
        // itemIndex was verified > -1 above
        id: items[itemIndex]!.id,
        // @ts-expect-error - TODO: fix this
        track: { ...omit(tempItem, 'tempId'), uploadId: tempItem.tempId },
      })
    }

    const updatedShortlist = {
      ...shortlist,
      tempItems: newTempItems ? stripFiles(newTempItems) : newTempItems,
      items: newItems,
    }
    return updateShortlist(state, updatedShortlist)
  },

  [SHORTLIST_TEMP_ITEM_PROGRESS_UPDATE](
    state: State,
    { shortlistId, update },
  ): State {
    const shortlist: Shortlist | null | undefined = state.byId[shortlistId]
    if (!shortlist) return state
    const { tempItems, items } = shortlist
    const tempItemIndex = (tempItems ?? []).findIndex(
      t => t.tempId === update.tempId,
    )
    const itemIndex = (items ?? []).findIndex(
      t => t.track.tempId === update.tempId,
    )
    // ignore update if item no longer exists
    if (itemIndex === -1 && (tempItemIndex === -1 || !tempItems)) return state
    const newTempItems =
      tempItemIndex > -1 && tempItems
        ? updateItem(tempItems, tempItemIndex, {
            // tempItemIndex was verified > -1 above
            ...tempItems[tempItemIndex]!,
            progress: update.progress,
            status: update.status || tempItems[tempItemIndex]!.status,
            externalUploadId: update.externalUploadId ?? undefined,
          })
        : tempItems
    const updatedShortlist = { ...shortlist, tempItems: newTempItems }
    return updateShortlist(state, updatedShortlist)
  },

  [SHORTLIST_TEMP_ITEM_INSERT](
    state: State,
    { shortlistId, tempItems, position, targetSection },
  ): State {
    const shortlist = state.byId[shortlistId]
    if (!shortlist) return state
    const targetPosition = isNaN(parseInt(position, 10))
      ? shortlist.items.length
      : position
    const newItems = stripFiles(tempItems).map((item, index) => ({
      id: targetPosition + index,
      tempId: item.tempId,
      track: { ...item, groups: [] },
    }))
    const updatedShortlist = {
      ...shortlist,
      items: __REACT_NATIVE__
        ? shortlist.items
        : adjustItemIds([
            ...shortlist.items.slice(0, targetPosition),
            ...newItems,
            ...shortlist.items.slice(targetPosition),
          ]),
      sections: shortlist.sections.map(section => {
        if (
          section.position >= targetPosition &&
          section.id !== targetSection?.id
        ) {
          return { ...section, position: section.position + newItems.length }
        }

        return section
      }),
      tempItems: [...(shortlist.tempItems ?? []), ...stripFiles(tempItems)],
    }
    return updateShortlist(state, updatedShortlist)
  },

  [SHORTLIST_TEMP_ITEM_REMOVE](state: State, { shortlistId, tempItem }): State {
    const shortlist = state.byId[shortlistId]
    if (!shortlist) return state
    const { items, sections, tempItems } = shortlist
    const tempItemIndex = tempItems
      ? tempItems.findIndex(t => t.tempId === tempItem.tempId)
      : -1
    const itemIndex = items.findIndex(it => it.track.tempId === tempItem.tempId)
    if (tempItemIndex === -1 && itemIndex === -1) return state
    const updatedShortlist = {
      ...shortlist,
      items:
        itemIndex > -1 ? adjustItemIds(deleteItem(items, itemIndex)) : items,
      sections:
        itemIndex > -1
          ? adjustSectionsOnRemovedTrackIndexes(shortlist, [itemIndex])
          : sections,
      tempItems:
        tempItems && tempItemIndex > -1
          ? deleteItem(tempItems, tempItemIndex)
          : tempItems,
    }
    return updateShortlist(state, updatedShortlist)
  },

  [SHORTLIST_UPLOADS_CANCEL](state: State): State {
    return transformShortlists(state, shortlist => {
      const removedTrackIndexes: number[] = []

      for (let i = 0; i < shortlist.items.length; i++) {
        // i is within bounds since loop limit is shortlist.items.length
        const item = shortlist.items[i]!

        if (item.track.tempId) {
          removedTrackIndexes.push(i)
        }
      }

      return {
        ...shortlist,
        items: adjustItemIds(
          (shortlist.items ?? []).filter(item => !item.track.tempId),
        ),
        sections: adjustSectionsOnRemovedTrackIndexes(
          shortlist,
          removedTrackIndexes,
        ),
        tempItems: [],
      }
    })
  },

  [SHORTLIST_PLAYLIST_TRACKS_ADD](state: State, { result }): State {
    const targetShortlist = state.byId[result.shortlistId]
    if (!targetShortlist) return state
    const shortlistItems = targetShortlist.items
    const shortlistTempItems = targetShortlist.tempItems
    // we don't support copying uploading items between shortlists currently
    // as it will require updating items in multiple shortlists and raise
    // question about what to do when user cancels upload in one of the shortlists:
    // should we remove cancelled upload everywhere or keep it going in others?
    const filteredTracks = result.tracks.filter(t => {
      if (!t.tempId) return true
      return (
        (shortlistTempItems ?? []).find(
          tempItem => tempItem.tempId === t.tempId,
        ) || shortlistItems.find(item => item.track.uploadId === t.tempId)
      )
    })
    let addedToItems = shortlistItems.filter(item => {
      return item.track.id
        ? !filteredTracks.some(track =>
            shortlistItemTrackComparator(item, track),
          )
        : !filteredTracks.some(track => track.tempId === item.track.tempId)
    })
    const newItems = filteredTracks
      .map(t => {
        if (t.tempId) {
          // we want to use updated track instead of stale temporary track
          const existingItem = shortlistItems.find(
            st => st.track.uploadId === t.tempId,
          )
          if (existingItem) return { ...existingItem.track, uploadId: t.tempId }
        }

        return t
      })
      .map(t =>
        t.tempId
          ? {
              track: t,
              tempId: t.tempId,
            }
          : {
              track: t,
            },
      )

    if (isNaN(parseInt(result.position, 10))) {
      addedToItems.push(...newItems)
    } else {
      addedToItems = [
        ...addedToItems.slice(0, result.position),
        ...newItems,
        ...addedToItems.slice(result.position),
      ]
    }

    addedToItems = adjustItemIds(addedToItems)
    const targetPosition = isNaN(parseInt(result.position, 10))
      ? addedToItems.length
      : result.position
    const adjustedSections = adjustSectionsOnMovedTracks(
      // targetShortlist was verified defined above via early return
      targetShortlist,
      filteredTracks,
      targetPosition,
      result.targetSection,
    )
    const updatedShortlist = {
      ...targetShortlist,
      saved: false,
      items: addedToItems,
      sections: adjustedSections,
      sort_key: 'custom',
      sort_direction: null,
    }
    return updateShortlist(state, updatedShortlist)
  },

  [LOAD_SAVED_STATE](
    state: State,
    {
      savedState,
    }: {
      savedState: Shortlist[]
    },
  ): State {
    return {
      ...state,
      visibleItems: savedState.map(s => s.id),
      byId: savedState.reduce((acc, s) => ({ ...acc, [s.id]: s }), {}),
    }
  },

  [PLAYLIST_DELETE_BATCH_SUCCESS](state: State, { playlistIds }): State {
    return transformShortlists(state, s => {
      return !playlistIds.includes(s.playlist_id)
        ? s
        : (omit(s, 'playlist_id') as Shortlist)
    })
  },

  [PLAYLIST_VERSION_DELETE_SUCCESS](state: State, action): State {
    return transformShortlists(state, s => {
      return s.playlist_id !== action.playlist.playlist_id
        ? s
        : (omit(s, 'playlist_id') as Shortlist)
    })
  },

  [TRACK_DELETE_BATCH_SUCCESS](state: State, { trackIds }): State {
    return transformShortlists(state, shortlist => {
      if (!shortlist.items) return shortlist
      const filteredItems = adjustItemIds(
        shortlist.items.filter(item => {
          return trackIds.indexOf(item.track.id) === -1
        }),
      )
      const adjustedSections = adjustSectionsOnRemovedTracks(
        shortlist,
        trackIds,
      )
      return {
        ...shortlist,
        items: filteredItems,
        sections: adjustedSections,
        track_aliases: shortlist.track_aliases
          ? omit(shortlist.track_aliases, trackIds)
          : shortlist.track_aliases,
      }
    })
  },

  [TRACK_MERGE_SUCCESS](state: State, { masterTrack, tracksToMerge }): State {
    const trackIds = tracksToMerge.map(t => t.id)
    return transformShortlists(state, shortlist => {
      if (!shortlist.items) return shortlist
      const shortlistContainsMasterTrack = shortlist.items.some(item => {
        return item.track.id === masterTrack.id
      })
      const lastItemToReplace = findLastIndex(shortlist.items, item => {
        return trackIds.indexOf(item.track.id) > -1
      })
      if (lastItemToReplace === -1) return shortlist
      const filteredItems = shortlist.items
        .filter((item, index) => {
          return (
            trackIds.indexOf(item.track.id) === -1 ||
            (index === lastItemToReplace && !shortlistContainsMasterTrack)
          )
        })
        .map((item, index) => {
          return trackIds.indexOf(item.track.id) === -1
            ? { ...item, id: index }
            : { ...item, id: index, track: masterTrack }
        })
      const trackIdsToRemove =
        lastItemToReplace > -1 && !shortlistContainsMasterTrack
          ? trackIds.filter(
              // lastItemToReplace was verified > -1 above
              id => id !== shortlist.items[lastItemToReplace]!.track.id,
            )
          : trackIds
      const adjustedSections = adjustSectionsOnRemovedTracks(
        shortlist,
        trackIdsToRemove,
      )
      return {
        ...shortlist,
        items: filteredItems,
        sections: adjustedSections,
        track_aliases: shortlist.track_aliases
          ? omit(shortlist.track_aliases, trackIds)
          : shortlist.track_aliases,
      }
    })
  },

  [SHORTLIST_SECTION_ADD](
    state: State,
    { shortlistId, name, position, place },
  ): State {
    const shortlist = state.byId[shortlistId]
    if (!shortlist) return state
    const { sections } = shortlist
    const insertPosition = findLastIndex(sections, s => s.position === position)
    let sectionIndex = 0

    if (insertPosition === -1) {
      sectionIndex = 0
    } else if (
      (insertPosition === 0 || insertPosition >= 0) &&
      place === 'top'
    ) {
      sectionIndex = 0
    } else {
      sectionIndex = insertPosition + 1
    }

    const updatedShortlist = {
      ...shortlist,
      saved: false,
      sections: [
        ...sections.slice(0, sectionIndex),
        {
          id: cuid(),
          name,
          position,
        },
        ...sections.slice(sectionIndex),
      ].sort((a, b) => {
        return a.position - b.position
      }),
    }
    return updateShortlist(state, updatedShortlist)
  },

  [SHORTLIST_SECTION_REMOVE](state: State, { shortlistId, sectionId }): State {
    const shortlist = state.byId[shortlistId]
    if (!shortlist) return state
    const updatedShortlist = {
      ...shortlist,
      saved: false,
      sections: shortlist.sections.filter(s => s.id !== sectionId),
    }
    return updateShortlist(state, updatedShortlist)
  },

  [SHORTLIST_SECTION_UPDATE](
    state: State,
    { shortlistId, sectionId, name, position, index },
  ): State {
    const shortlist = state.byId[shortlistId]
    if (!shortlist) return state
    const { sections } = shortlist
    const sectionIndex = sections.findIndex(s => s.id === sectionId)
    const sectionTargetIndex =
      index === undefined || index === null ? sectionIndex : index
    const update =
      position >= 0
        ? { ...sections[sectionIndex], name, position }
        : { ...sections[sectionIndex], name }
    // Remove the section from the current position
    const sectionsWithoutCurrent = sections.filter((_, i) => i !== sectionIndex)
    // Insert the updated section at the new target index
    const updatedIndexSections = [
      ...sectionsWithoutCurrent.slice(0, sectionTargetIndex),
      update,
      ...sectionsWithoutCurrent.slice(sectionTargetIndex),
    ]
    const updatedShortlist = {
      ...shortlist,
      saved: false,
      sections: position
        ? updatedIndexSections.sort((a, b) => a.position - b.position)
        : updatedIndexSections,
    }
    return updateShortlist(state, updatedShortlist)
  },

  [SHORTLIST_ACTIVATE](state: State, { shortlistId }): State {
    const shortlist = state.byId[shortlistId]
    if (!shortlist || shortlist.hidden) return state
    const updatedShortlist = { ...shortlist, isActive: true }
    return {
      ...updateShortlist(state, updatedShortlist),
      shortlistActivation: shortlistId,
    }
  },

  [SHORTLIST_DEACTIVATE](state) {
    return { ...state, shortlistActivation: null }
  },

  [START_SAVING_STATE](state) {
    return { ...state, loaded: true, error: null }
  },

  [LOAD_SAVED_STATE_ERROR](state, { error }) {
    return { ...state, loaded: false, loading: false, error: error.message }
  },

  [SHORTLIST_RESET]() {
    return { ...initialState, loaded: true }
  },
})
